import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  MODAL_LEAD_FORMS_DUPLICATE_AND_EDIT,
  MODAL_LEAD_FORMS_EMPTY_TEXT,
  MODAL_LEAD_FORMS_VIEW,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { getLanguageLabel } from "../../utils/catalogs/languageOptions";
import { BORDER_RADIUS, LINKEDIN } from "../../utils/constants";
import {
  canSelectLeadFormForCampaign,
  getLeadFormLanguageOption,
} from "../../utils/linkedInLeadFormLanguage";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import IconButton from "../Buttons/IconButton";

export const LeadFormsList = ({
  leadForms = [],
  selectedItem,
  onChangeSelectedItem,
  onDuplicateLeadForm = () => {},
  onViewLeadForm = () => {},
  lastItemRef,
  campaignLanguage = "en",
}) => {
  const { t } = useTranslationApp();

  const leadFormsSorted = [...leadForms].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  if (leadForms.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t(MODAL_LEAD_FORMS_EMPTY_TEXT)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <RadioGroup
        value={selectedItem || ""}
        onChange={(e) => {
          const nextId = e.target.value;
          const nextForm = leadForms.find((leadForm) => leadForm.id === nextId);
          if (
            !canSelectLeadFormForCampaign(nextForm?.language, campaignLanguage)
          ) {
            return;
          }
          onChangeSelectedItem(nextId);
        }}
      >
        {leadFormsSorted.map((leadForm, index) => {
          const isLastItem = index === leadFormsSorted.length - 1;
          const isSelectable = canSelectLeadFormForCampaign(
            leadForm.language,
            campaignLanguage
          );
          const languageOption = getLeadFormLanguageOption(leadForm.language);
          const languageLabel = languageOption
            ? getLanguageLabel(LINKEDIN, languageOption.value, t)
            : leadForm.language;

          return (
            <Box
              key={leadForm.id}
              ref={isLastItem ? lastItemRef : undefined}
              sx={{
                mb: 1,
                p: 1.5,
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: BORDER_RADIUS,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
              }}
            >
              <FormControlLabel
                value={leadForm.id}
                disabled={!isSelectable}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                    >
                      {leadForm.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {languageLabel}
                    </Typography>
                  </Box>
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  m: 0,
                  alignItems: "flex-start",
                  opacity: isSelectable ? 1 : 0.5,
                  "& .MuiFormControlLabel-label": {
                    minWidth: 0,
                    width: "100%",
                  },
                }}
              />
              <IconButton
                tooltipText={MODAL_LEAD_FORMS_VIEW}
                edge={false}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onViewLeadForm(leadForm);
                }}
                sx={{ p: 0.5, mt: -0.25 }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                tooltipText={MODAL_LEAD_FORMS_DUPLICATE_AND_EDIT}
                edge={false}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onDuplicateLeadForm(leadForm);
                }}
                sx={{ p: 0.5, mt: -0.25 }}
              >
                <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          );
        })}
      </RadioGroup>
    </Box>
  );
};
